import { format } from 'date-fns';
import FundOpportunityChatResponse, {
  FundOpportunityChatResponseAddress,
} from 'FundOpportunityChatResponse';
import ExtendedLiquid from '../../services/ExtendedLiquid';
import { formatNumber } from '../../utils/formatNumber';
import { FundOpportunitySector, SECTOR_LABELS } from './fundOpportunitySectorOptions';

export const preferredFieldOrder = [
  'name',
  'summaryTitle',
  'closeDate',
  'fundSize',
  'fundDuration',
  'interest',
  'returnOnInvestment',
  'siteArea',
  'currentValuation',
  'addresses',
  'lga',
  'state',
  'country',
  'sector',
  'seniorDebtLvr',
];

const fieldFormatters: { [key: string]: (val: any) => string } = {
  closeDate: (val: string) => format(new Date(val), 'd/M/yyyy'),
  currentValuation: (val: number) => ExtendedLiquid.currencyMillions(val, true),
  fundDuration: (val: number) => `${val} months`,
  fundSize: (val: number) => ExtendedLiquid.currencyMillions(val, true),
  returnOnInvestment: (val: number) => `${formatNumber(val)}%`,
  siteArea: (val: number) => `${val}m²`,
  sector: (val: FundOpportunitySector[]) => val.map((s) => SECTOR_LABELS[s] || s).join(', '),
  addresses: (val: FundOpportunityChatResponseAddress[]) =>
    val.map((a) => a.fullAddress).join('\n'),
};

export const formatPopulatedFieldValue = (value: any, key: string): string => {
  const actual = 'value' in value ? value.value : value;
  return fieldFormatters[key]?.(actual) ?? String(actual);
};

// Fields not listed here (sector, zoning, addresses) keep their existing dedicated
// read-only/multi-select UI rather than a plain text or number box.
const fieldInputTypes: { [key: string]: 'text' | 'number' | 'date' } = {
  closeDate: 'date',
  currentValuation: 'number',
  fundDuration: 'number',
  fundSize: 'number',
  interest: 'number',
  returnOnInvestment: 'number',
  seniorDebtLvr: 'number',
  siteArea: 'number',
};

export const getEditableFieldInputType = (key: string): 'text' | 'number' | 'date' =>
  fieldInputTypes[key] ?? 'text';

// Multi-value fields can't round-trip through a single text box.
const NON_EDITABLE_FIELDS = new Set(['sector', 'zoning', 'addresses']);

export const isFieldEditable = (key: string): boolean => !NON_EDITABLE_FIELDS.has(key);

export function hasPopulatedFundOpportunityFields(
  data: FundOpportunityChatResponse['currentData'],
): boolean {
  if (!data) return false;

  return Object.entries(data).some(([fieldName, field]) => {
    if (fieldName === 'tempFundOpportunityId') {
      return false;
    }

    const val = 'value' in (field as any) ? (field as any).value : field;
    return (
      val !== null && val !== undefined && val !== '' && !(Array.isArray(val) && val.length === 0)
    );
  });
}
